import { useQuery } from "@tanstack/react-query";
import { useDynamicSupabase } from "./useDynamicSupabase";

export const useDashboardData = () => {
  const { supabase, isReady } = useDynamicSupabase();

  return useQuery({
    queryKey: ["dashboard-data"],
    queryFn: async () => {
      if (!supabase || !isReady) {
        throw new Error("Supabase client not ready");
      }

      // جلب جميع البيانات بشكل متوازي لتحسين الأداء
      const [
        clientsResult,
        bookingsResult,
        hotelsResult,
        toursResult,
        tasksResult
      ] = await Promise.all([
        // جلب العملاء مع الشركات
        supabase
          .from("clients")
          .select(`
            *,
            companies:company_id (name_ar),
            creator:created_by (name)
          `),
        
        // جلب الحجوزات مع بيانات الموظفين والعملاء
        supabase
          .from("package_bookings")
          .select(`
            *,
            clients!inner(name_ar, status, companies(name_ar)),
            employees(name, department)
          `),
        
        // جلب الفنادق
        supabase
          .from("hotels")
          .select("country, city, stars"),
        
        // جلب الجولات السياحية
        supabase
          .from("tourist_tours")
          .select(`
            *,
            countries(name_ar, name_en),
            cities(name_ar, name_en)
          `),
        
        // جلب المهام المتأخرة
        supabase
          .from("tasks")
          .select("title, due_date, assigned_to, status")
          .eq("status", "pending")
          .lt("due_date", new Date().toISOString())
      ]);

      // التحقق من وجود أخطاء
      if (clientsResult.error) throw clientsResult.error;
      if (bookingsResult.error) throw bookingsResult.error;
      if (hotelsResult.error) throw hotelsResult.error;
      if (toursResult.error) throw toursResult.error;
      if (tasksResult.error) throw tasksResult.error;

      const clients = clientsResult.data || [];
      const bookings = bookingsResult.data || [];
      const hotels = hotelsResult.data || [];
      const tours = toursResult.data || [];
      const overdueTasks = tasksResult.data || [];

      // تحليل المبيعات
      const salesAnalysis = analyzeSales(bookings);
      
      // تحليل العملاء
      const clientAnalysis = analyzeClients(clients);
      
      // تحليل البلدان
      const countryAnalysis = analyzeCountries(hotels, tours);
      
      // تحليل التنبيهات
      const notifications = analyzeNotifications(clients, overdueTasks, bookings);

      // تحليل العروض الجديد
      const offersAnalysis = analyzeOffers(bookings);

      // تحليل الموظفين والعروض
      const employeeOffersAnalysis = analyzeEmployeeOffers(bookings);

      // تحليل الدول والعروض
      const countryOffersAnalysis = analyzeCountryOffers(bookings, clients);

      return {
        sales: salesAnalysis,
        clients: clientAnalysis,
        countries: countryAnalysis,
        notifications,
        offers: offersAnalysis,
        employeeOffers: employeeOffersAnalysis,
        countryOffers: countryOffersAnalysis,
        // إحصائيات عامة
        stats: {
          totalClients: clients.length,
          totalBookings: bookings.length,
          totalCompanies: new Set(clients.map(c => c.companies?.name_ar).filter(Boolean)).size,
          totalRevenue: salesAnalysis.totalRevenue
        }
      };
    },
    enabled: isReady && !!supabase,
    staleTime: 2 * 60 * 1000, 
    gcTime: 5 * 60 * 1000, 
    refetchOnWindowFocus: false,
  });
};

// دالة تحليل العروض حسب التاريخ
const analyzeOffers = (bookings: any[]) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const todayOffers = bookings.filter(booking => {
    const bookingDate = new Date(booking.created_at);
    return bookingDate.toDateString() === today.toDateString();
  });

  const yesterdayOffers = bookings.filter(booking => {
    const bookingDate = new Date(booking.created_at);
    return bookingDate.toDateString() === yesterday.toDateString();
  });

  const monthOffers = bookings.filter(booking => {
    const bookingDate = new Date(booking.created_at);
    return bookingDate >= startOfMonth;
  });

  return {
    today: {
      count: todayOffers.length,
      offers: todayOffers,
      revenue: todayOffers.reduce((sum, offer) => sum + (parseFloat(String(offer.price)) || 0), 0)
    },
    yesterday: {
      count: yesterdayOffers.length,
      offers: yesterdayOffers,
      revenue: yesterdayOffers.reduce((sum, offer) => sum + (parseFloat(String(offer.price)) || 0), 0)
    },
    month: {
      count: monthOffers.length,
      offers: monthOffers,
      revenue: monthOffers.reduce((sum, offer) => sum + (parseFloat(String(offer.price)) || 0), 0)
    }
  };
};

// دالة تحليل عروض الموظفين
const analyzeEmployeeOffers = (bookings: any[]) => {
  const employeeStats = bookings.reduce((acc: any, booking) => {
    const employeeName = booking.employees?.name || 'غير محدد';
    const clientStatus = booking.clients?.status || 'غير محدد';
    
    if (!acc[employeeName]) {
      acc[employeeName] = {
        name: employeeName,
        totalOffers: 0,
        statusBreakdown: {},
        department: booking.employees?.department || 'غير محدد'
      };
    }
    
    acc[employeeName].totalOffers += 1;
    acc[employeeName].statusBreakdown[clientStatus] = (acc[employeeName].statusBreakdown[clientStatus] || 0) + 1;
    
    return acc;
  }, {});

  return Object.values(employeeStats).sort((a: any, b: any) => b.totalOffers - a.totalOffers);
};

// دالة تحليل عروض الدول
const analyzeCountryOffers = (bookings: any[], clients: any[]) => {
  // ربط العروض بالدول من خلال معلومات العملاء
  const countryStats = bookings.reduce((acc: any, booking) => {
    const client = clients.find(c => c.id === booking.client_id);
    const country = client?.companies?.name_ar || 'غير محدد'; // استخدام الشركة كمؤشر للدولة
    const clientStatus = booking.clients?.status || 'غير محدد';
    
    if (!acc[country]) {
      acc[country] = {
        name: country,
        totalOffers: 0,
        statusBreakdown: {},
        revenue: 0
      };
    }
    
    acc[country].totalOffers += 1;
    acc[country].statusBreakdown[clientStatus] = (acc[country].statusBreakdown[clientStatus] || 0) + 1;
    acc[country].revenue += parseFloat(String(booking.price)) || 0;
    
    return acc;
  }, {});

  return Object.values(countryStats).sort((a: any, b: any) => b.totalOffers - a.totalOffers);
};

// دالة تحليل المبيعات
const analyzeSales = (bookings: any[]) => {
  const monthlySales = bookings.reduce((acc: any, booking) => {
    const month = new Date(booking.created_at).toLocaleString('ar-SA', { 
      year: 'numeric', 
      month: 'long' 
    });
    const price = parseFloat(String(booking.price)) || 0;
    if (!acc[month]) {
      acc[month] = { total: 0, count: 0 };
    }
    acc[month].total += price;
    acc[month].count += 1;
    return acc;
  }, {});

  const employeeSales = bookings.reduce((acc: any, booking) => {
    if (booking.employees?.name) {
      const employeeName = booking.employees.name;
      const price = parseFloat(String(booking.price)) || 0;
      if (!acc[employeeName]) {
        acc[employeeName] = { total: 0, count: 0, department: booking.employees.department };
      }
      acc[employeeName].total += price;
      acc[employeeName].count += 1;
    }
    return acc;
  }, {});

  const topEmployees = Object.entries(employeeSales)
    .map(([name, data]: [string, any]) => ({
      name,
      total: data.total,
      count: data.count,
      department: data.department,
      average: data.total / data.count
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  const totalRevenue = bookings.reduce((sum, booking) => {
    return sum + (parseFloat(String(booking.price)) || 0);
  }, 0);

  return {
    totalRevenue,
    totalBookings: bookings.length,
    monthlySales,
    topEmployees,
    averageOrderValue: totalRevenue / bookings.length || 0
  };
};

// دالة تحليل العملاء
const analyzeClients = (clients: any[]) => {
  const statusAnalysis = clients.reduce((acc: any, client) => {
    const status = client.status || 'غير محدد';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const monthlyAnalysis = clients.reduce((acc: any, client) => {
    const month = new Date(client.created_at).toLocaleString('ar-SA', { 
      year: 'numeric', 
      month: 'long' 
    });
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, {});

  return {
    totalClients: clients.length,
    statusAnalysis,
    monthlyAnalysis,
    recentClients: clients.slice(-5)
  };
};

// دالة تحليل البلدان
const analyzeCountries = (hotels: any[], tours: any[]) => {
  const hotelsByCountry = hotels.reduce((acc: any, hotel) => {
    const country = hotel.country;
    if (!acc[country]) {
      acc[country] = { hotels: 0, averageStars: 0, cities: new Set() };
    }
    acc[country].hotels += 1;
    acc[country].averageStars += hotel.stars;
    acc[country].cities.add(hotel.city);
    return acc;
  }, {});

  Object.keys(hotelsByCountry).forEach(country => {
    hotelsByCountry[country].averageStars = 
      hotelsByCountry[country].averageStars / hotelsByCountry[country].hotels;
    hotelsByCountry[country].cities = hotelsByCountry[country].cities.size;
  });

  const toursByCountry = tours.reduce((acc: any, tour) => {
    const countryName = tour.countries?.name_ar || 'غير محدد';
    const price = parseFloat(String(tour.price)) || 0;
    if (!acc[countryName]) {
      acc[countryName] = { tours: 0, totalValue: 0 };
    }
    acc[countryName].tours += 1;
    acc[countryName].totalValue += price;
    return acc;
  }, {});

  const topCountries = Object.entries(toursByCountry)
    .map(([country, data]: [string, any]) => ({
      country,
      tours: data.tours,
      totalValue: data.totalValue,
      averagePrice: data.totalValue / data.tours || 0,
      hotels: hotelsByCountry[country]?.hotels || 0
    }))
    .sort((a, b) => b.totalValue - a.totalValue)
    .slice(0, 8);

  return {
    topCountries,
    hotelsByCountry,
    toursByCountry,
    totalCountries: Object.keys(toursByCountry).length
  };
};

// دالة تحليل التنبيهات
const analyzeNotifications = (clients: any[], overdueTasks: any[], bookings: any[]) => {
  const notifications = [];

  // العملاء المتأخرين
  const pendingClients = clients.filter(client => 
    ['waiting', 'pricing_requested'].includes(client.status) &&
    new Date(client.created_at) < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  );

  if (pendingClients.length > 0) {
    notifications.push({
      id: "pending-clients",
      type: "warning",
      title: "عملاء بانتظار المتابعة",
      message: `لديك ${pendingClients.length} عميل بانتظار المتابعة لأكثر من أسبوع`,
      priority: "high",
      count: pendingClients.length
    });
  }

  // المهام المتأخرة
  if (overdueTasks.length > 0) {
    notifications.push({
      id: "overdue-tasks",
      type: "error",
      title: "مهام متأخرة",
      message: `لديك ${overdueTasks.length} مهمة متأخرة تحتاج لمتابعة`,
      priority: "urgent",
      count: overdueTasks.length
    });
  }

  // الحجوزات التي تحتاج تأكيد
  const pendingBookings = bookings.filter(booking => 
    booking.status === 'pending' &&
    new Date(booking.booking_date) < new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
  );

  if (pendingBookings.length > 0) {
    notifications.push({
      id: "pending-bookings",
      type: "info",
      title: "حجوزات تحتاج تأكيد",
      message: `لديك ${pendingBookings.length} حجز بحاجة لتأكيد`,
      priority: "medium",
      count: pendingBookings.length
    });
  }

  const priorityOrder = { urgent: 3, high: 2, medium: 1, low: 0 };
  return notifications.sort((a, b) => 
    priorityOrder[b.priority as keyof typeof priorityOrder] - 
    priorityOrder[a.priority as keyof typeof priorityOrder]
  );
};
